<template>
  <div>
    <ECharts :options="options" auto-resize :style="{ height: height }" watchShallow></ECharts>
  </div>
</template>
<script>
/* eslint-disable import/extensions, import/no-unresolved */

import Vue from 'vue';
import ECharts from 'vue-echarts/components/ECharts.vue';
import 'echarts/lib/chart/bar';
import 'echarts/lib/component/tooltip';
import 'echarts/lib/component/title';
import 'echarts/lib/component/legend';

function p(value, decimal) {
  return value.toFixed(decimal).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

Vue.component('chart', ECharts);
const xAxis = {
  type: 'category',
  data: ['1st Quarter', 'Middle 50%', '4th Quarter', 'Overall'],
};
const option = {
  tooltip: {
    trigger: 'axis',
    axisPointer: {
      type: 'shadow',
    },
    formatter(items) {
      // eslint-disable-next-line
      const colorSpan = (color) => `<span style="display:inline-block;margin-right:5px;border-radius:10px;width:9px;height:9px;background-color:${color}"></span>`;
      let series = '';
      if (items.length > 0) {
        series = items[0].axisValueLabel;
      }
      const labels = items
        .filter((i) => i.data.value !== 0)
        .map((i) => {
          const strItem = colorSpan(i.color);
          return `${strItem}${i.seriesName} (p.a.): ${i.data.name}`;
        });
      return `<strong>${series}</strong><br>${labels.join('<br>')}`;
    },
  },
  legend: {},
  grid: {
    left: '3%',
    right: '4%',
    bottom: '3%',
    containLabel: true,
  },
  yAxis: [
    {
      type: 'value',
      max: 100,
    },
  ],
  series: [],
};
const labelOption = {
  normal: {
    show: true,
    position: 'inside',
    distance: 15,
    align: 'center',
    verticalAlign: 'middle',
    rotate: 0,
    formatter({ data }) {
      return data.value < 2 ? '' : data.name;
    },
    fontSize: 14,
    rich: {
      name: {
        textBorderColor: '#fff',
      },
    },
  },
};

function getMean(array) {
  if (array === null || typeof array === 'undefined') {
    return 0;
  }
  if (array.length === 0) return 0;
  const sum = array.reduce((a, b) => a + b);
  return sum / array.length;
}

function getTotalMean(incumbents, allBenefitTypes) {
  return allBenefitTypes.reduce((acc, cur) => {
    const vals = incumbents
      .map((inc) => inc[cur.value])
      .filter((v) => v !== 0)
      .map((v) => (v < 0 ? 0 : v));
    const mean = getMean(vals) || 0;
    return acc + mean;
  }, 0);
}

export default Vue.extend({
  props: {
    companyBenefits: {
      type: Array,
    },
    height: {
      type: String,
    },
    exchangeRate: Object,
  },
  computed: {
    benefits() {
      let incumbents = [];
      this.companyBenefits.forEach((cb) => {
        incumbents = incumbents.concat(...cb.positions);
      });
      if (incumbents.length === 0) {
        return [];
      }
      const { usdDivisor } = this.exchangeRate;
      const { allBenefitTypes } = this;
      if (incumbents.length < 8) {
        const totalMean = getTotalMean(incumbents, allBenefitTypes);
        return allBenefitTypes
          .map((b) => {
            const vals = incumbents
              .map((inc) => inc[b.value])
              .filter((v) => v !== 0)
              .map((v) => (v < 0 ? 0 : v));
            const mean = getMean(vals) || 0;
            return {
              name: b.text,
              type: 'bar',
              stack: 'comp',
              label: labelOption,
              data: [
                {
                  value: (mean / totalMean) * 100,
                  name: `${p(mean * usdDivisor, 0)} (${p((mean / totalMean) * 100, 1)}%)`,
                },
              ].filter((v) => v !== 0),
            };
          })
          .filter((v) => v.data.length > 0);
      }

      // use an array to get all incumbents
      // sort by total_cost
      // split into 4 arrays
      // calculate mean for each array
      incumbents.sort((a, b) => a.totalCostUSD - b.totalCostUSD);
      const count = Math.round(incumbents.length / 4);
      const remainder = incumbents.length % 4;
      let q1 = [];
      let q2 = [];
      let q3 = [];
      let q4 = [];
      if (remainder === 1) {
        q1 = incumbents.splice(0, count);
        q2 = incumbents.splice(0, count);
        q3 = incumbents.splice(0, count);
        q4 = incumbents.splice(0, count + 1);
      } else if (remainder === 2) {
        q1 = incumbents.splice(0, count - 1);
        q2 = incumbents.splice(0, count);
        q3 = incumbents.splice(0, count);
        q4 = incumbents.splice(0, count - 1);
      } else {
        q1 = incumbents.splice(0, count);
        q2 = incumbents.splice(0, count);
        q3 = incumbents.splice(0, count);
        q4 = incumbents.splice(0, count);
      }

      const q1TotalMean = getTotalMean(q1, allBenefitTypes);
      const q4TotalMean = getTotalMean(q4, allBenefitTypes);
      const totalMean = getTotalMean([...q1, ...q2, ...q3, ...q4], allBenefitTypes);
      const m50TotalMean = getTotalMean([...q2, ...q3], allBenefitTypes);

      return allBenefitTypes
        .map((b) => {
          const q1Vals = q1
            .map((inc) => inc[b.value])
            .filter((v) => v !== 0)
            .map((v) => (v < 0 ? 0 : v));
          const m50Vals = [...q2, ...q3]
            .map((inc) => inc[b.value])
            .filter((v) => v !== 0)
            .map((v) => (v < 0 ? 0 : v));
          const q4Vals = q4
            .map((inc) => inc[b.value])
            .filter((v) => v !== 0)
            .map((v) => (v < 0 ? 0 : v));
          const vals = [...q1, ...q2, ...q3, ...q4]
            .map((inc) => inc[b.value])
            .filter((v) => v !== 0)
            .map((v) => (v < 0 ? 0 : v));
          const q1mean = getMean(q1Vals) || 0;
          const m50mean = getMean(m50Vals) || 0;
          const q4mean = getMean(q4Vals) || 0;
          const mean = getMean(vals) || 0;
          return {
            name: b.text,
            type: 'bar',
            stack: 'comp',
            label: labelOption,
            data: [
              {
                value: (q1mean / q1TotalMean) * 100,
                name: `${p(q1mean * usdDivisor, 0)} (${p((q1mean / q1TotalMean) * 100, 1)}%)`,
              },
              {
                value: (m50mean / m50TotalMean) * 100,
                name: `${p(m50mean * usdDivisor, 0)} (${p((m50mean / m50TotalMean) * 100, 1)}%)`,
              },
              {
                value: (q4mean / q4TotalMean) * 100,
                name: `${p(q4mean * usdDivisor, 0)} (${p((q4mean / q4TotalMean) * 100, 1)}%)`,
              },
              {
                value: (mean / totalMean) * 100,
                name: `${p(mean * usdDivisor, 0)} (${p((mean / totalMean) * 100, 1)}%)`,
              },
            ],
          };
        })
        .filter((v) => v.data.length > 0);
    },
    xAxis() {
      let incumbentsCount = 0;
      this.companyBenefits.forEach((cb) => {
        incumbentsCount += cb.positions.length;
      });
      if (incumbentsCount >= 8) {
        return xAxis;
      }
      return {
        type: 'category',
        data: ['Mean'],
      };
    },
    options() {
      return {
        ...option,
        series: this.benefits,
        xAxis: this.xAxis,
      };
    },
  },
  data() {
    return {
      allBenefitTypes: [
        { value: 'salaryUSD', text: 'Annual Basic Salary' },
        { value: 'allowanceUSD', text: 'Fixed Allowance' },
        { value: 'shortTermIncentiveUSD', text: 'Annual Variable Cash' },
        { value: 'longTermIncentiveUSD', text: 'Long-term Incentive' },
        { value: 'totalPerquisitesUSD', text: 'Perquisites' },
        { value: 'totalSocialBenefitsUSD', text: 'Pension' },
      ],
    };
  },
  methods: {},
  components: {
    ECharts,
  },
});
</script>
<style scoped>
.echarts {
  /* height: 100%; */
  width: 100%;
}
</style>
